import { FileProp } from '../types/shared.types';
import { ContractStatus } from '@equaliq/api-model';

/**
 * Contract-related utility functions and components
 */

/**
 * Maps contract status enum values to user-friendly display strings
 */
export const getContractStatusDisplay = (status: ContractStatus): string => {
  switch (status) {
    case ContractStatus.processing:
      return "Processing";
    case ContractStatus.awaiting_upload:
      return "Awaiting Upload";
    case ContractStatus.extracting_text:
      return "Extracting Text";
    case ContractStatus.eq_generation:
      return "Generating EQ Analysis";
    case ContractStatus.iq_generation:
      return "Generating IQ Analysis";
    case ContractStatus.variable_extraction:
      return "Extracting Variables";
    case ContractStatus.contract_markup:
      return "Processing Contract Markup";
    case ContractStatus.tts_generation:
      return "Generating TTS Audio";
    case ContractStatus.complete:
      return "Analysis Complete";
    case ContractStatus.error:
      return "Stuck Processing";
    default:
      return "Unknown Status";
  }
};

/**
 * Checks if a contract is fully analyzed and ready for viewing
 */
export const isContractAnalysisReady = (status: ContractStatus): boolean => {
  return status === ContractStatus.complete;
};

/**
 * Returns the CSS color variable for a given contract type
 * Used for consistent theming across contract components
 */
export const ContractTypeColor = ({ type }: { type: string }) => {
  switch (type) {
    case 'management':
      return "var(--color-management)";
    case 'recording':
      return "var(--color-recording)";
    case 'producer':
      return "var(--color-producer)";
    case 'publishing':
      return "var(--color-publishing)";
    case 'services':
      return "var(--color-services)";
    case 'tbd':
      return "var(--color-tbd)";
  }
};

/**
 * Sort contracts by uploadedOn date (most recent first)
 */
export const sortContracts = (contracts: FileProp[]): FileProp[] => {
  return [...contracts].sort((a, b) => {
    const dateA = new Date(a.uploadedOn);
    const dateB = new Date(b.uploadedOn);

    // Check for invalid dates, push them to the bottom
    if (isNaN(dateA.getTime())) return 1;
    if (isNaN(dateB.getTime())) return -1;

    // Sort in descending order (most recent first)
    return dateB.getTime() - dateA.getTime();
  });
};

/**
 * Filter contracts by ownership type
 * TODO: Fix isOwner logic in backend - currently hardcoded to false for org contracts
 * For now, always show all contracts regardless of filterType
 */
export const filterContractsByOwnership = (
  contracts: FileProp[],
  filterType: 'owned' | 'shared' | 'all'
): FileProp[] => {
  // Temporarily disabled - always show all contracts
  // Backend needs to properly set isOwner based on ownerUserId === userId
  return contracts;

  // Original logic (commented out):
  // if (filterType === 'owned') {
  //   return contracts.filter(contract => contract.isOwner);
  // }
  // if (filterType === 'shared') {
  //   return contracts.filter(contract => !contract.isOwner);
  // }
  // return contracts; // 'all'
};

/**
 * Filter contracts by search query
 */
export const filterContractsBySearch = (
  contracts: FileProp[], 
  searchQuery: string
): FileProp[] => {
  if (!searchQuery.trim()) return contracts;
  
  const normalizedQuery = searchQuery.toLowerCase();
  return contracts.filter(contract =>
    contract.name.toLowerCase().includes(normalizedQuery) ||
    contract.type.toLowerCase().includes(normalizedQuery) ||
    contract.status.toLowerCase().includes(normalizedQuery)
  );
};